import { useEffect, useMemo, useState } from 'react';

import {
  ActionBar,
  ChakraSimpleGrid,
  FAIconName,
  ProfileAboutMeCard,
  ProfileInfoCard,
  SkeletonLoader,
  useToastMessage,
} from '@equip.health/ui';
import { capitalize } from 'lodash';
import { useParams } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';

import {
  ProfileEventsAdmin,
  ProfileEventsMaud,
} from '~/analytics/profileEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import {
  PortalComponent,
  useComponentPortalContext,
} from '~/lib/contexts/ComponentPortalContext';
import { UserMode, useModeContext } from '~/lib/contexts/ModeContext';
import { getSupervisorFullName } from '~/lib/util/profile.util';
import { useInviteProviderMutation } from '~/store/services/providers';

import ProfilePermissions from './ProfilePermissions';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { useProviderProfileData } from '~/lib/hooks/useProviderProfileData';

const ProfileInfo = () => {
  const TOP_LEVEL_ID = 'profile-info';
  const { mode } = useModeContext();
  const isAdmin = mode === UserMode.ADMIN;
  const { hasManageUserRole } = useGlobalContext();
  const params = useParams<UserProfileParams>();
  const providerExternalId = params.id ?? params[0].split('/')[0];
  const { providerAboutMe: isProviderAboutMeEnabled } = useFlags<LDFlags>();
  const { providerData, avatarUrl, isLoading } = useProviderProfileData(
    providerExternalId,
    isAdmin,
  );

  const [showEditAboutMeAccordion, setShowEditAboutMeAccordion] =
    useState<boolean>(false);

  const [showEditProfileAccordion, setEditProfileAccordion] =
    useState<boolean>(false);
  const [inviteProvider, { isSuccess }] = useInviteProviderMutation();

  const { setComponentVisibility } = useComponentPortalContext();

  const providerStatus = providerData?.userStatus as ProviderUserStatus;
  const { createToast } = useToastMessage();
  const { trackEvent } = useAnalytics();
  useEffect(() => {
    if (showEditAboutMeAccordion) {
      setShowEditAboutMeAccordion(false);
      setComponentVisibility({
        component: PortalComponent.EDIT_ABOUTME_ACCORDION,
        isExpanded: true,
        isVisible: true,
        data: providerData,
      });
    }
  }, [showEditAboutMeAccordion]);

  useEffect(() => {
    if (showEditProfileAccordion) {
      setEditProfileAccordion(false);
      setComponentVisibility({
        component: PortalComponent.ADD_EDIT_PROFILE_ACCORDION,
        data: providerData,
        isExpanded: true,
        isVisible: true,
      });
    }
  }, [showEditProfileAccordion]);

  const handleProfileInfoEditClick = () => {
    trackEvent(
      isAdmin
        ? ProfileEventsAdmin.adminProfilePageEditButtonClicked
        : ProfileEventsMaud.maudProfilePageEditButtonClicked,
      {
        source: isAdmin
          ? AnalyticsSourceValues.PROFILE_TAB_ADMIN
          : AnalyticsSourceValues.PROFILE_TAB_MAUD,
      },
    );
    setEditProfileAccordion(true);
  };

  const handleAboutMeEditClick = () => {
    trackEvent(
      isAdmin
        ? ProfileEventsAdmin.adminAboutMeEditButtonClicked
        : ProfileEventsMaud.maudAboutMeEditButtonClicked,
      {
        source: isAdmin
          ? AnalyticsSourceValues.PROFILE_TAB_ADMIN
          : AnalyticsSourceValues.PROFILE_TAB_MAUD,
      },
    );
    setShowEditAboutMeAccordion(true);
  };

  const providerCredentials = useMemo(
    () => providerData?.credentialsDetails?.map((item) => item.code),
    [providerData],
  );

  const providerPronouns = useMemo(
    () => providerData?.pronounsDetails?.map((item) => item.name),
    [providerData],
  );

  const providerLanguages = useMemo(
    () => providerData?.languagesDetails?.map((item) => item.name),
    [providerData],
  );
  const updatedPronouns = providerPronouns?.map((item) => {
    const pronounText = item?.toLowerCase() === 'other';
    return pronounText
      ? capitalize(providerData?.otherPronounText) || 'Other'
      : item;
  });

  const shouldShowActionBar =
    isAdmin && hasManageUserRole && !providerData?.hasSetPassword && !isSuccess;
  const onResendEmail = () => {
    inviteProvider({ providerExternalId })
      .unwrap()
      .then(() => {
        createToast({
          icon: FAIconName.checkRegular,
          id: 'resend-mail-success_toast',
          text: 'Email resent',
          variant: 'success',
        });
      })
      .catch(() => {
        createToast({
          icon: FAIconName.exclamationTriangleRegular,
          id: 'resend-mail-error_toast',
          text: "We're sorry, we were unable to resend email at this time. Please try again.",
          variant: 'critical',
        });
      });
  };
  const supervisorFullName = getSupervisorFullName(
    providerData?.supervisor?.firstName,
    providerData?.supervisor?.lastName,
  );
  const providerfirstName =
    providerData?.displayFirstName || providerData?.firstName;
  const providerMiddleName =
    providerData?.displayMiddleName || providerData?.middleName;
  const providerLastName =
    providerData?.displayLastName || providerData?.lastName;
  const providerSuffix = providerData?.displaySuffix || providerData?.suffix;
  const updatedPronunciation = capitalize(providerData?.pronunciation);

  return (
    <SkeletonLoader id={`${TOP_LEVEL_ID}__loader`} isLoaded={!isLoading}>
      <ChakraSimpleGrid
        gap="36px"
        marginBottom="20px"
        maxHeight="80vh"
        overflowY="auto"
        padding="30px 24px 36px 0px"
      >
        {shouldShowActionBar && (
          <ActionBar
            barIcon={FAIconName.infoCircleRegular}
            buttonIcon={FAIconName.shareRegular}
            buttonText="Resend email"
            id={`${TOP_LEVEL_ID}-no-password`}
            onButtonClick={onResendEmail}
            text="This user has not created a password"
            variant="warning"
          />
        )}
        <ProfileInfoCard
          avatarUrl={avatarUrl}
          capacity={providerData?.capacity?.name}
          credentials={providerCredentials}
          email={providerData?.email}
          firstName={providerfirstName}
          handleEditClick={handleProfileInfoEditClick}
          hoursPerWeek={providerData?.hoursPerWeek?.toString()}
          id={TOP_LEVEL_ID}
          isAdmin={isAdmin}
          languages={providerLanguages}
          lastName={providerLastName}
          middleName={providerMiddleName}
          nationalProviderIdentifier={providerData?.nationalProviderIdentifier}
          phoneNumber={providerData?.phoneNumber}
          pronouns={updatedPronouns}
          pronunciation={updatedPronunciation}
          providerTypeName={providerData?.providerTypeDetails?.name}
          stateOfResidence={providerData?.stateOfResidenceDetails?.name}
          suffix={providerSuffix}
          supervisor={supervisorFullName}
          timezone={providerData?.timeZoneDetails?.label}
          userStatus={providerStatus}
        />
        {isProviderAboutMeEnabled && (
          <ProfileAboutMeCard
            aboutMe={providerData?.connectWithMe}
            experience={providerData?.professionalExperience}
            handleEditClick={handleAboutMeEditClick}
          />
        )}
        {isAdmin && (
          <ProfilePermissions
            permissions={providerData?.permissions}
            providerType={providerData?.providerTypeDetails?.name}
            roles={providerData?.roles}
          />
        )}
      </ChakraSimpleGrid>
    </SkeletonLoader>
  );
};

export default ProfileInfo;
